// Platform → Services → Storage: where every customer's uploads live.
//
// Three states, made legible up top: platform settings in force (saved
// here), env vars in force (from deployment), or local disk (the
// single-instance default). The secret is write-only — the field is always
// blank; blank on save means "keep what's stored". Saving verifies the
// bucket with a real write first, so a typo cannot be put in force.
//
// This file also holds the three cards every Services tab is built from
// (R12): the status card, its Source row and the form card. They live here
// rather than in PlatformSettings.tsx, which imports this file, so the two
// never import each other.
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import {
  PlatformStorageState, StorageMigrationStatus, StorageSettingsBody,
  clearPlatformStorage, getPlatformStorage, migratePlatformStorage, savePlatformStorage, testPlatformStorage,
} from '../../api/admin';
import { RowCard } from '../../components/fs';
import { useToast } from '../../components/Toast';
import { SectionEdits, useDirty, useReportEdits } from '../../components/unsaved';
import { usePlatform } from './PlatformLayout';

/** The tone of a Source row's state: in force, off, working-but-fragile, broken. */
export type SourceTone = 'ok' | 'off' | 'warn' | 'bad';

/**
 * The card each Services tab leads with: what is in force, before the form
 * that changes it. Its rows are `RowCard`s; the old emoji banner is its
 * Source row.
 */
export function StatusCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="fs-card pl-svc-status" aria-label={`${title} status`}>
      <div className="st-card-head"><h2>{title}</h2></div>
      {children}
    </section>
  );
}

/** Where the settings in force came from — the first thing to know on this screen. */
export function SourceRow({ tone, title, blurb, label = 'Source' }: {
  tone: SourceTone; title: string; blurb?: ReactNode; label?: string;
}) {
  return (
    <RowCard label={label}>
      <span className={`st-state ${tone}`}>{title}</span>
      {blurb && <div className="pl-svc-blurb">{blurb}</div>}
    </RowCard>
  );
}

/** The form under a status card: a titled card with its fields in one column. */
export function FormCard({ title, children, className }: { title: string; children: ReactNode; className?: string }) {
  return (
    <section className={`fs-card pl-svc-formcard${className ? ` ${className}` : ''}`}>
      <div className="st-card-head"><h2>{title}</h2></div>
      <div className="st-card-body pl-svc-form">{children}</div>
    </section>
  );
}

const fmtBytes = (n: number) => {
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)} GB`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)} MB`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(0)} KB`;
  return `${n} B`;
};

const SOURCE_COPY: Record<PlatformStorageState['source'], [string, string]> = {
  platform: ['Bucket (platform settings)', 'Uploads are stored in the bucket configured below.'],
  env: ['Bucket (environment)', 'Uploads are stored in the bucket set by S3_* environment variables. Saving settings here overrides them.'],
  disk: ['Local disk', "Uploads are stored on this server's own disk — fine for one instance, lost to the others the moment there are two."],
};

export default function PlatformStorage({ onEdits }: {
  /** Storage is a tab of Platform services; the tab row asks before it switches. */
  onEdits?: (e: SectionEdits | null) => void;
} = {}) {
  const { role } = usePlatform();
  const [state, setState] = useState<PlatformStorageState | null>(null);
  const [migration, setMigration] = useState<StorageMigrationStatus | null>(null);
  const [bucket, setBucket] = useState('');
  const [endpoint, setEndpoint] = useState('');
  const [region, setRegion] = useState('');
  const [accessKeyId, setAccessKeyId] = useState('');
  const [secret, setSecret] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toast = useToast();

  const snapshot = useMemo(
    () => ({ bucket, endpoint, region, accessKeyId, secret }),
    [bucket, endpoint, region, accessKeyId, secret]
  );
  const { dirty, markSaved } = useDirty(snapshot, !!state);
  useReportEdits(onEdits, dirty, () => onSave());

  const apply = (s: PlatformStorageState, m?: StorageMigrationStatus) => {
    setState(s);
    if (m) setMigration(m);
    setBucket(s.platform?.bucket || '');
    setEndpoint(s.platform?.endpoint || '');
    setRegion(s.platform?.region || '');
    setAccessKeyId(s.platform?.accessKeyId || '');
    setSecret('');
  };

  const load = () => {
    getPlatformStorage()
      .then((r) => apply(r.storage, r.migration))
      .catch((e) => setError(e.message || 'Could not load storage settings.'));
  };
  useEffect(() => { load(); return () => { if (pollTimer.current) clearTimeout(pollTimer.current); }; }, []);

  // While a migration runs, keep the numbers moving.
  useEffect(() => {
    if (migration?.state !== 'running') return;
    pollTimer.current = setTimeout(() => {
      getPlatformStorage().then((r) => setMigration(r.migration)).catch(() => undefined);
    }, 2000);
    return () => { if (pollTimer.current) clearTimeout(pollTimer.current); };
  }, [migration]);

  const body = (): StorageSettingsBody => ({
    bucket: bucket.trim(),
    endpoint: endpoint.trim() || undefined,
    region: region.trim() || undefined,
    accessKeyId: accessKeyId.trim() || undefined,
    secretAccessKey: secret || undefined,
  });

  async function onTest() {
    setBusy(true); setError('');
    try {
      const r = await testPlatformStorage(bucket.trim() ? body() : undefined);
      if (r.result.ok) toast('The bucket answered: wrote, read back and deleted a probe object.', { kind: 'info' });
      else setError(r.result.error || 'The bucket could not be reached.');
    } catch (e: any) { setError(e.message || 'Test failed.'); }
    finally { setBusy(false); }
  }

  async function onSave() {
    setBusy(true); setError('');
    try {
      const r = await savePlatformStorage(body());
      apply(r.storage);
      markSaved();
      toast('Storage saved and in force — new uploads land in the bucket. Other app instances pick it up within a minute.');
    } catch (e: any) {
      setError(e.message || 'Save failed.');
      throw e;   // so "Save and leave" stays put rather than losing the keys
    }
    finally { setBusy(false); }
  }

  async function onClear() {
    if (!confirmClear) { setConfirmClear(true); return; }
    setConfirmClear(false); setBusy(true); setError('');
    try {
      const r = await clearPlatformStorage();
      apply(r.storage);
      markSaved();
      toast('Platform settings cleared — uploads follow the environment variables, or local disk without them.', { kind: 'info' });
    } catch (e: any) { setError(e.message || 'Clear failed.'); }
    finally { setBusy(false); }
  }

  async function onMigrate() {
    setBusy(true); setError('');
    try {
      const r = await migratePlatformStorage();
      setMigration(r.migration);
      if (!r.started && r.migration.state !== 'running') setError('Migration did not start — is an S3 target in force?');
    } catch (e: any) { setError(e.message || 'Migration failed to start.'); }
    finally { setBusy(false); }
  }

  if (!state) {
    return (
      <section className="fs-card pl-svc-status">
        <div className="st-card-body">{error ? <div className="ad-err">{error}</div> : <span className="st-muted">Loading…</span>}</div>
      </section>
    );
  }

  const [sourceTitle, sourceBlurb] = SOURCE_COPY[state.source];
  const readOnly = role !== 'superadmin';
  const running = migration?.state === 'running';

  return (
    <>
      <StatusCard title="Upload storage">
        <RowCard label="Bucket">
          {state.platform?.bucket
            ? <span className="st-mono">{state.platform.bucket}</span>
            : state.source === 'env' ? <span>Set by the environment</span> : <span className="st-state off">None — local disk</span>}
        </RowCard>
        <SourceRow tone={state.source === 'disk' ? 'warn' : 'ok'} title={sourceTitle} blurb={sourceBlurb} />
      </StatusCard>

      <FormCard title="S3-compatible bucket">
        {error && <div className="ad-err">{error}</div>}
        {readOnly && (
          <p className="pl-svc-readonly">
            Changing storage needs the Super Admin platform role — shown read-only.
          </p>
        )}

        <div className="fs-field">
          <label htmlFor="svc-storage-bucket">Bucket</label>
          <input id="svc-storage-bucket" value={bucket} disabled={readOnly} placeholder="form-magic-uploads"
            onChange={(e) => setBucket(e.target.value)} />
        </div>
        <div className="fs-field">
          <label htmlFor="svc-storage-endpoint">Endpoint (blank for AWS S3)</label>
          <input id="svc-storage-endpoint" value={endpoint} disabled={readOnly} placeholder="https://minio.internal:9000 · https://…r2.cloudflarestorage.com"
            onChange={(e) => setEndpoint(e.target.value)} />
          <div className="hint">Any S3-compatible store: MinIO, Cloudflare R2, Backblaze B2… A custom endpoint switches to path-style addressing automatically.</div>
        </div>
        <div className="pl-svc-pair region">
          <div className="fs-field">
            <label htmlFor="svc-storage-region">Region</label>
            <input id="svc-storage-region" value={region} disabled={readOnly} placeholder="us-east-1" onChange={(e) => setRegion(e.target.value)} />
          </div>
          <div className="fs-field">
            <label htmlFor="svc-storage-akid">Access key ID</label>
            <input id="svc-storage-akid" value={accessKeyId} disabled={readOnly} onChange={(e) => setAccessKeyId(e.target.value)} />
          </div>
        </div>
        <div className="fs-field">
          <label htmlFor="svc-storage-secret">Secret access key</label>
          <input id="svc-storage-secret" type="password" value={secret} disabled={readOnly}
            placeholder={state.platform?.hasSecret ? '•••••••• (saved — leave blank to keep)' : ''}
            onChange={(e) => setSecret(e.target.value)} autoComplete="new-password" />
          <div className="hint">Encrypted at rest and never shown again. Leave blank on later saves to keep the stored one.</div>
        </div>

        <div className="pl-svc-actions">
          <button className="fs-btn primary" disabled={busy || readOnly || !bucket.trim()}
            onClick={() => { void onSave().catch(() => undefined); }}>
            {busy ? 'Working…' : 'Verify & save'}
          </button>
          <button className="fs-btn neutral" disabled={busy} onClick={() => void onTest()}>Test connection</button>
          <span className="pl-svc-spacer" />
          {state.platform && !readOnly && (
            <button className="fs-btn danger sm" disabled={busy} onClick={() => void onClear()}>
              {confirmClear ? 'Confirm — back to env/disk' : 'Clear platform settings'}
            </button>
          )}
        </div>
        <p className="pl-svc-foot">
          Saving verifies the bucket with a real write first. Files already uploaded stay where they
          are and keep serving — use the migration below to move local-disk files into the bucket.
        </p>
      </FormCard>

      <FormCard title="Migrate local files into the bucket" className="pl-svc-migrate">
        <p className="pl-svc-lead">
          Copies everything under this server's uploads directory into the active bucket (storage
          ledger included) so old attachments survive a move to multiple instances. Safe to re-run;
          local files are never deleted — remove the uploads directory by hand once verified.
        </p>
        {migration && migration.state !== 'idle' && (
          <div className="pl-svc-migration" role="status">
            {migration.state === 'running' && (
              <><span className="fs-pill info">Copying</span> <b>{migration.files}</b> file(s), {fmtBytes(migration.bytes)} so far.</>
            )}
            {migration.state === 'done' && (
              <><span className="fs-pill ok">Done</span> {migration.files} file(s), {fmtBytes(migration.bytes)}{migration.orphans ? `; ${migration.orphans} orphaned file(s) skipped` : ''}.</>
            )}
            {migration.state === 'error' && <><span className="fs-pill danger">Failed</span> {migration.error}</>}
          </div>
        )}
        <div className="pl-svc-actions">
          <button className="fs-btn neutral" disabled={busy || readOnly || state.source === 'disk' || running}
            onClick={() => void onMigrate()}>
            {running ? 'Copying…' : 'Copy local files to the bucket'}
          </button>
        </div>
        {state.source === 'disk' && (
          <div className="pl-svc-foot">Set a bucket first — there is nowhere to migrate to yet.</div>
        )}
      </FormCard>
    </>
  );
}
